import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, exhaustMap, finalize, map, Observable, shareReplay, switchMap, tap, timer } from 'rxjs';

/**
 * utilities
 */
import { IJwtWithRefreshResponse, JwtUtils } from '@core/utilities';

/**
 * interfaces
 */
import {
  IAuthLogin,
  IAuthJwtService,
  IAuthLoginService,
  IAuthLoggedInService,
  IAuthControlsService,
  IAuthSessionManagementService,
} from './interfaces';

/**
 * network
 */
import { SessionOrchestratorService } from '@network';

/**
 * services
 */
import { AUTH_API_LOGIN, AUTH_API_SESSION_MANAGEMENT, IAuthLoginApi, IAuthSessionManagementApi } from './services';

/**
 * Comprehensive authentication service for JWT token management
 * @class AuthenticationService
 * @description Provides secure JWT token management, user session handling, and browser storage integration with SSR support.
 * Implements multiple authentication interfaces to provide a complete authentication solution.
 * 
 * @implements {IAuthJwtService} JWT token management functionality
 * @implements {IAuthLoggedInService} Login state management functionality  
 * @implements {IAuthControlsService} JWT control and storage operations
 * @implements {IAuthSessionManagementService} Session management functionality

 */
@Injectable()
export class AuthenticationService
  implements IAuthJwtService, IAuthLoggedInService, IAuthControlsService, IAuthSessionManagementService, IAuthLoginService {

  /**
   * Platform detection flag for SSR safety
   * @private
   * @readonly
   * @type {boolean}
   * @description Indicates whether the application is running in a browser environment.
   * Used to prevent localStorage access during server-side rendering.
   */
  private readonly _isBrowser: boolean = true;

  /**
   * LocalStorage key for JWT token storage
   * @private
   * @readonly
   * @type {string}
   * @description The key used to store JWT tokens in browser localStorage.
   * Follows naming convention with underscore prefix for internal use.
   */
  private readonly _JWT_STORAGE_KEY = '_JWT_';

  /**
   * Observable stream of refresh token in flight
   * @private
   * @readonly
   * @type {Observable<string>}
   * @description Emits the current refresh token in flight whenever it changes.
   */
  private _refreshInFlight$: Observable<string> | null = null;

  /**
   * Observable stream of refresh token in flight
   * @public
   * @readonly
   * @type {Observable<string> | null}
   * @description Emits the current refresh token in flight whenever it changes.
   */
  public get refreshInFlight$(): Observable<string> | null {
    return this._refreshInFlight$;
  }

  /**
   * BehaviorSubject for refreshing state management
   * @private
   * @readonly
   * @type {BehaviorSubject<boolean>}
   * @description Emits the current refreshing state whenever it changes.
   */
  private readonly _refreshing$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable stream of refreshing state
   * @public
   * @readonly
   * @type {Observable<boolean>}
   * @description Emits the current refreshing state whenever it changes.
   */
  public get refreshing$(): Observable<boolean> {
    return this._refreshing$.asObservable();
  }

  /**
   * BehaviorSubject for login in progress state management
   * @private
   * @readonly
   * @type {BehaviorSubject<boolean>}
   * @description Emits true when a login request is in progress, false otherwise.
   * Used to track the login status and display loading indicators.
   * ```
   */
  private readonly _loginInProgressSubject$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable stream of login in progress state
   * @public
   * @readonly
   * @type {Observable<boolean>}
   * @description Emits true when a login request is in progress, false otherwise.
   * Used to track the login status and display loading indicators.
   */
  public get loginInProgress$(): Observable<boolean> {
    return this._loginInProgressSubject$.asObservable();
  }

  /**
   * Starts the login in progress state
   * @public
   * @method startLogin
   * @description Starts the login in progress state.
   * This method is used to start the login in progress state.
   * 
   * @example
   * ```typescript
   * this.authService.startLogin();
   */
  public startLogin(): void {
    this._loginInProgressSubject$.next(true);
  }

  /**
   * Stops the login in progress state
   * @public
   * @method stopLogin
   * @description Stops the login in progress state.
   * This method is used to stop the login in progress state.
   * 
   * @example
   * ```typescript
   * this.authService.stopLogin();
   * ```
   */
  public stopLogin(): void {
    this._loginInProgressSubject$.next(false);
  }

  /**
   * BehaviorSubject for JWT response state management
   * @private
   * @readonly
   * @type {BehaviorSubject<IJwtWithRefreshResponse | null>}
   * @description Central state management for JWT responses.
   * Provides both synchronous and asynchronous access to authentication state.
   */
  private readonly _jwt$: BehaviorSubject<IJwtWithRefreshResponse | null> = new BehaviorSubject<IJwtWithRefreshResponse | null>(null);

  /**
   * Observable stream of JWT response objects
   * @public
   * @readonly
   * @type {Observable<IJwtWithRefreshResponse | null>}
   * @description Emits the current JWT response whenever it changes.
   * Subscribers will receive updates when authentication state changes.
   * 
   * @example
   * ```typescript
   * this.authService.jwt$.subscribe(jwt => {
   *   if (jwt) {
   *     console.log('User authenticated');
   *   } else {
   *     console.log('User not authenticated');
   *   }
   * });
   * ```
   */
  public get jwt$(): Observable<IJwtWithRefreshResponse | null> {
    return this._jwt$.asObservable()
  }

  /**
   * Updates the JWT observable with a new value
   * @public
   * @method setJwt$
   * @param {IJwtWithRefreshResponse | null} value - The new JWT response value to set
   * @description Sets the JWT observable to emit the provided value.
   * This method triggers all subscribers to receive the new authentication state.
   * 
   * @example
   * ```typescript
   * // Set new JWT response
   * this.authService.setJwt$(jwtResponse);
   * 
   * // Clear authentication state
   * this.authService.setJwt$(null);
   * ```
   */
  public setJwt$(value: IJwtWithRefreshResponse | null): void {
    this._jwt$.next(value);
  }

  /**
   * Current JWT response object
   * @public
   * @readonly
   * @type {IJwtWithRefreshResponse | null}
   * @description Returns the current JWT response or null if not available.
   * Provides synchronous access to the current authentication state.
   * 
   * @example
   * ```typescript
   * const currentJwt = this.authService.jwt;
   * if (currentJwt) {
   *   console.log('Access token:', currentJwt.jwt);
   *   console.log('Refresh token:', currentJwt.jwt_refresh);
   * }
   * ```
   */
  public get jwt(): IJwtWithRefreshResponse | null {
    return this._jwt$.value
  }


  /**
   * Observable stream of JWT access tokens
   * @public
   * @readonly
   * @type {Observable<string | null>}
   * @description Emits the current JWT access token whenever it changes.
   * Derived from the JWT response observable for convenient token access.
   * 
   * @example
   * ```typescript
   * this.authService.accessToken$.subscribe(accessToken => {
   *   if (accessToken) {
   *     // Use token for API requests
   *     this.httpClient.get('/api/data', {
   *       headers: { Authorization: `Bearer ${accessToken}` }
   *     });
   *   }
   * });
   * ```
   */
  public readonly accessToken$!: Observable<string | null>;

  /**
   * Current JWT access token
   * @public
   * @readonly
   * @type {string | null}
   * @description Returns the current JWT access token or null if not available.
   * Extracted from the current JWT response for immediate use in API calls.
   * 
   * @example
   * ```typescript
   * const accessToken = this.authService.accessToken;
   * if (accessToken) {
   *   // Make authenticated API request
   *   this.httpClient.get('/api/user', {
   *     headers: { Authorization: `Bearer ${accessToken}` }
   *   });
   * }
   * ```
   */
  public get accessToken(): string | null {

    //
    if (this.jwt == null) return null;

    // ES6 Destruction
    const { jwt } = this.jwt;

    return jwt;
  }

  /**
   * Observable stream of JWT refresh tokens
   * @public
   * @readonly
   * @type {Observable<string | null>}
   * @description Emits the current JWT refresh token whenever it changes.
   * Used for automatic token refresh operations when access tokens expire.
   * 
   * @example
   * ```typescript
   * this.authService.refreshToken$.subscribe(refreshToken => {
   *   if (refreshToken) {
   *     // Store refresh token for token renewal
   *     this.tokenRefreshService.setRefreshToken(refreshToken);
   *   }
   * });
   * ```
   */
  public readonly refreshToken$!: Observable<string | null>;

  /**
   * Current JWT refresh token
   * @public
   * @readonly
   * @type {string | null}
   * @description Returns the current JWT refresh token or null if not available.
   * Used for obtaining new access tokens when the current one expires.
   * 
   * @example
   * ```typescript
   * const refreshToken = this.authService.refreshToken;
   * if (refreshToken) {
   *   // Use refresh token to get new access token
   *   this.authService.refreshAccessToken(refreshToken);
   * }
   * ```
   */
  public get refreshToken(): string | null {

    //
    if (this.jwt == null) return null;

    // ES6 Destruction
    const { jwt_refresh } = this.jwt;

    return jwt_refresh;
  }

  /**
   * Observable stream of login state changes
   * @public
   * @readonly
   * @type {Observable<boolean>}
   * @description Emits the current login state whenever it changes.
   * Derived from the token observable, providing a boolean representation of authentication status.
   * 
   * @example
   * ```typescript
   * this.authService.isLoggedIn$.subscribe(isLoggedIn => {
   *   if (isLoggedIn) {
   *     this.router.navigate(['/dashboard']);
   *   } else {
   *     this.router.navigate(['/login']);
   *   }
   * });
   * ```
   */
  public get isLoggedIn$(): Observable<boolean> {
    return this.accessToken$.pipe<boolean>(map(Boolean))
  }

  /**
   * Current login state
   * @public
   * @readonly
   * @type {boolean}
   * @description Returns true if user is currently logged in, false otherwise.
   * Provides synchronous access to authentication status for immediate checks.
   * 
   * @example
   * ```typescript
   * if (this.authService.isLoggedIn) {
   *   // User is authenticated, proceed with protected operations
   *   this.loadUserData();
   * } else {
   *   // Redirect to login page
   *   this.router.navigate(['/login']);
   * }
   * ```
   */
  public get isLoggedIn(): boolean {
    return Boolean(this.accessToken)
  }

  /**
   * Constructor for AuthenticationService
   * @constructor
   * @param {Object} _platformId - Angular platform ID for SSR detection
   * @description Initializes the authentication service with platform detection and observable streams.
   * Sets up token observables and initializes JWT from browser storage if available.
   * 
   * @example
   * ```typescript
   * // Service is automatically injected by Angular DI
   * constructor(@Inject(AUTH_JWT) private authJwt: IAuthJwtService) {}
   * ```
   */
  constructor(
    private readonly _router: Router,
    @Inject(PLATFORM_ID)
    private readonly _platformId: Object,
    @Inject(AUTH_API_SESSION_MANAGEMENT)
    private readonly _authSessionManagementApi: IAuthSessionManagementApi,
    @Inject(AUTH_API_LOGIN)
    private readonly _authLoginApi: IAuthLoginApi,
    private readonly _sessionOrchestrator: SessionOrchestratorService,
  ) {

    // check platform
    this._isBrowser = isPlatformBrowser(_platformId);

    // stream to current token
    this.accessToken$ = this.jwt$.pipe(map(x => x?.jwt ?? null));
    // stream to current token
    this.refreshToken$ = this.jwt$.pipe(map(x => x?.jwt_refresh ?? null));

    // set obs$ if jwt is back from browser storage
    this._initJwtFromBrowserStorage();
  
    // setup auto refresh token
    this._setupAutoRefreshToken();

  }

  /**
   * Initializes JWT from browser storage
   * @private
   * @method _initJwtFromBrowserStorage
   * @description Retrieves JWT from localStorage and updates the observable if available.
   * Only executes in browser environment to prevent SSR errors.
   * 
   * @example
   * ```typescript
   * // Called automatically during service initialization
   * // No manual invocation required
   * ```
   */
  private _initJwtFromBrowserStorage(): void {

    // void ssr
    if (!this._isBrowser) return;

    try {

      // get JWT_STORAGE
      const _jwt_string = localStorage.getItem(this._JWT_STORAGE_KEY);

      // check if JWT_STORAGE value not null
      if (_jwt_string == null) return;


      // get jwt from storage
      const jwt = JSON.parse(_jwt_string);

      // update observable
      this.setJwt$(jwt);

    }
    catch (error) {
      console.error('Error initializing JWT from browser storage', error);
    }

  }

  /**
   * Sets the access token in the JWT response
   * @public
   * @method setAccessToken$
   * @param {string} accessToken - The access token to set
   * @description Sets the access token in the JWT response.
   * This method triggers all subscribers to receive the new authentication state.
   * 
   * @example
   * ```typescript
   * // Set new access token
   * this.authService.setAccessToken$('new-access-token');
   * ```
   */
  public setAccessToken$(accessToken: string): void {

    // void
    if (accessToken == null) {
      console.warn(`(setAccessToken), arg accessToken is => ${accessToken}`)
      return;
    }

    // void
    if (this.refreshToken == null) {
      console.warn(`(setAccessToken), refreshToken is not available`)
      return;
    }

    // update authentication state
    this.updateAuthState({
      jwt: accessToken,
      jwt_refresh: this.refreshToken
    });

  }

  /**
   * Updates authentication state with new JWT
   * @public
   * @method updateAuthState
   * @param {IJwtWithRefreshResponse} jwt - The JWT response to update state with
   * @description Updates both the observable and storage with the new JWT response.
   * This is the primary method for setting authentication state after login.
   * 
   * @example
   * ```typescript
   * // After successful login
   * this.authService.updateAuthState({
   *   jwt: 'access_token_here',
   *   jwt_refresh: 'refresh_token_here'
   * });
   * ```
   */
  public updateAuthState(jwt: IJwtWithRefreshResponse): void {

    if (jwt == null) {
      console.warn(`(updateJwt), arg jwt is => ${jwt}`)
      return;
    }

    this.setJwt$(jwt);
    this.storeJwt(jwt);

  }

  /**
   * Stores JWT response in browser storage
   * @public
   * @method storeJwt
   * @param {IJwtWithRefreshResponse} jwt - The JWT response to store
   * @description Persists the JWT response to localStorage for session persistence.
   * Only executes in browser environment to prevent SSR errors.
   * 
   * @example
   * ```typescript
   * // Store JWT after receiving from server
   * this.authService.storeJwt({
   *   jwt: 'access_token',
   *   jwt_refresh: 'refresh_token'
   * });
   * ```
   */
  public storeJwt(jwt: IJwtWithRefreshResponse): void {

    if (jwt == null) {
      console.warn(`(storeJwt), arg jwt is => ${jwt}`)
      return;
    }

    // void ssr
    if (!this._isBrowser) return;

    localStorage.setItem(this._JWT_STORAGE_KEY, JSON.stringify(jwt))

  }

  /**
   * Clears current JWT from memory and storage
   * @public
   * @method clearJwt
   * @description Removes JWT from both observable state and browser storage.
   * This is the primary method for logging out users and clearing authentication state.
   * 
   * @example
   * ```typescript
   * // Logout user
   * this.authService.clearJwt();
   * 
   * // Redirect to login page
   * this.router.navigate(['/login']);
   * ```
   */
  public clearJwt(): void {

    // void
    if (this.jwt == null) {
      console.warn('jwt is already empty')
      return;
    }

    // clear Observable
    this.setJwt$(null);

    // clear browser storage
    this._clearJwtFromStorage();

  }

  /**
   * Removes JWT from localStorage
   * @private
   * @method _clearJwtFromStorage
   * @description Removes JWT from localStorage after checking platform and storage state.
   * Only executes in browser environment to prevent SSR errors.
   * 
   * @example
   * ```typescript
   * // Called automatically by clearJwt() method
   * // No manual invocation required
   * ```
   */
  private _clearJwtFromStorage(): void {

    // void ssr
    if (!this._isBrowser) return;

    // get jwt from storage
    const jwt = JSON.parse(localStorage.getItem(this._JWT_STORAGE_KEY) as string);

    // check if jwt is null
    if (jwt == null) {
      console.warn(`(_clearJwtFromStorage), jwt is already clear`)
      return;
    }

    localStorage.removeItem(this._JWT_STORAGE_KEY)

  }

  /**
   * Initializes the automatic token refresh mechanism.
   * 
   * @private
   * @method _setupAutoRefreshToken
   * @description Creates a self-sustaining RxJS stream that monitors the current Access Token and automatically schedules a refresh.
   * 
   * The logic flow is:
   * 1. Listens to `this.accessToken$` stream.
   * 2. When a token is emitted (Login or Refresh):
   *    - It calculates the remaining lifespan of the token using `JwtUtils`.
   *    - It starts a timer to fire slightly before expiration (default buffer is applied in `getRemainingTimeInMs`).
   *    - Uses `switchMap` to ensure that if a new token arrives (e.g., from an Interceptor refresh), the old timer is cancelled and a new one starts.
   * 3. When the timer fires:
   *    - It calls `refreshAccessTokenShared()` to fetch a new token.
   *    - On success, the new token is emitted to `accessToken$`, restarting the cycle (Loop).
   *    - On error, it catches the error to prevent crashing the stream (and optionally logs out).
   * 
   * Handles edge cases:
   * - **Logout:** If token is null, returns `NEVER` to pause the timer logic.
   * - **Already Expired:** If token is expired, the timer fires immediately (0ms delay).
   * - **Browser Only:** Checks `_isBrowser` to avoid running timers during Server-Side Rendering (SSR).
   */
  private _setupAutoRefreshToken(): void {

    this.accessToken$
      .pipe(
        switchMap(accessToken => {

          // if access token is null, return empty observable
          if (accessToken == null) return EMPTY;

          // get remaining time in milliseconds
          let remainingTime = new JwtUtils(accessToken).getRemainingTimeInMs(5);

          // if remaining time is less than or equal to 0, set to 0
          if (remainingTime <= 0) {
            remainingTime = 0;
          }

          return timer(remainingTime);
        }),
        // When timer fires, switch to the API call
        exhaustMap(() => this.refreshAccessTokenShared().pipe(
          catchError(err => {
            console.error('Auto-refresh by calc remaining time failed', err);
            return EMPTY;
          })
        ))
      ).subscribe()

  }

  /**
   * Refreshes the access token by making a request to the refresh endpoint.
   * @public
   * @method refreshAccessTokenShared
   * @returns {Observable<string>} Observable stream of the refreshed access token
   * @description Refreshes the access token by making a request to the refresh endpoint.
   */
  public refreshAccessTokenShared(): Observable<string> {

    // void return new observable
    if (this._refreshInFlight$) return this._refreshInFlight$;

    // get refresh token
    const refreshToken = this.refreshToken ?? '';
    if (!refreshToken) throw new Error('refreshToken is empty');

    // set refreshing to true
    this._refreshing$.next(true);

    // get new access token by refresh token
    this._refreshInFlight$ = this._authSessionManagementApi.getNewAccessTokenByRefreshToken(refreshToken).pipe(
      map(res => {
        // update jwt
        const nextState = {
          jwt: res.jwt,
          jwt_refresh: refreshToken
        };
        this.updateAuthState(nextState);
        return nextState.jwt;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      finalize(() => {
        this._refreshing$.next(false);
        this._refreshInFlight$ = null;
      })
    );

    return this._refreshInFlight$;
  }

  /**
   * 
   */
  public login(body: IAuthLogin, callback?: (response: IJwtWithRefreshResponse) => void): Observable<IJwtWithRefreshResponse> {

    // start login in progress state
    this.startLogin();

    // login api call
    return this._authLoginApi
                .login(body)
                .pipe(
                  tap(response => {
                    
                    // check if callback is a function
                    if (callback instanceof Function) {
                      callback(response);
                    }
                    else {
                      // update authentication state and navigate to system
                      this.updateAuthState(response);
                      this._router.navigate(['/system']);
                    }
                  }),
                  finalize(() => {
                    this.stopLogin();
                  })
                )

  }

  /**
   * Logs out the user by making a request to the logout endpoint.
   * @public
   * @method logout
   * @description Logs out the user by making a request to the logout endpoint.
   * This method is used to logout the user by making a request to the logout endpoint.
   * 
   * @example
   * ```typescript
   * // Logout user
   * this.authService.logout();
   * ```
   */
  public logout(): Observable<unknown> {

    // void
    if (this.accessToken == null) {
      console.warn('token is not available')
      return EMPTY;
    }

    // logout api call
    return this._authSessionManagementApi
      .logout(this.accessToken)
      .pipe(
        finalize(() => {
          this.clearJwt();
          this._sessionOrchestrator.abortGlobal();
          this._router.navigate(['/auth/login']);
        })
      )
  }

}
